import { HANDLES, INTEGRATED_HANDLE_STYLES } from "../catalog";
import type { HandleOrientation, HandleType, IntegratedHandleStyle, WardrobeModule } from "../types";

export const MIN_HANDLE_LENGTH_MM = 64;
export const MAX_HANDLE_LENGTH_MM = 1200;
export const MIN_HANDLE_EDGE_OFFSET_MM = 24;

export type HandleGeometry = Pick<
  WardrobeModule,
  | "handle"
  | "handleLength"
  | "handleCenterY"
  | "handleEdgeOffset"
  | "handleOrientation"
  | "integratedHandleStyle"
>;

/** Вид интегрированной ручки; по умолчанию — по всей высоте торца. */
export const integratedHandleStyle = (config: Pick<WardrobeModule, "integratedHandleStyle">): IntegratedHandleStyle =>
  config.integratedHandleStyle === "edgePart" || config.integratedHandleStyle === "top" || config.integratedHandleStyle === "topPart"
    ? config.integratedHandleStyle : "edge";

/** Участок по центру верхней кромки ящика: 40 % ширины фасада, не короче 150 мм (24.10). */
export const integratedTopPartLengthMm = (frontWidthMm: number) =>
  Math.round(Math.min(frontWidthMm, Math.max(150, frontWidthMm * 0.4)));

/** Длина выборки интегрированной ручки: вся кромка, участок или верхний край. */
export const integratedHandleLengthMm = (config: HandleGeometry, doorWidthMm: number, doorHeightMm: number) => {
  const style = integratedHandleStyle(config);
  if (style === "edge") return Math.round(doorHeightMm);
  if (style === "top") return Math.round(doorWidthMm);
  if (style === "topPart") return integratedTopPartLengthMm(doorWidthMm);
  return handleLengthMm(config, doorWidthMm, doorHeightMm);
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const handleSupportsOrientation = (handle: HandleType) =>
  handle === "bar" || handle === "recessed";

export const handleOrientation = (
  config: HandleGeometry
): HandleOrientation =>
  handleSupportsOrientation(config.handle) &&
  config.handleOrientation === "horizontal"
    ? "horizontal"
    : "vertical";

const defaultHandleLengthMm = (
  handle: HandleType,
  availableSpanMm: number
) => {
  if (handle === "profile") return Math.max(320, availableSpanMm * 0.72);
  if (handle === "integrated") return Math.max(150, availableSpanMm * 0.16);
  if (handle === "recessed") return 126;
  if (handle === "push" || handle === "glassGrip") return 0;
  return 220;
};

export const handleLengthRangeMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  const orientation = handleOrientation(config);
  const availableSpan =
    orientation === "horizontal" ? doorWidthMm : doorHeightMm;
  const maximum = Math.max(
    MIN_HANDLE_LENGTH_MM,
    Math.min(MAX_HANDLE_LENGTH_MM, Math.round(availableSpan) - 48)
  );
  return {
    min: Math.min(MIN_HANDLE_LENGTH_MM, maximum),
    max: maximum
  };
};

export const handleLengthMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  if (config.handle === "push" || config.handle === "glassGrip") return 0;
  const orientation = handleOrientation(config);
  const availableSpan =
    orientation === "horizontal" ? doorWidthMm : doorHeightMm;
  const range = handleLengthRangeMm(config, doorWidthMm, doorHeightMm);
  // Длина 0 оставалась от стекла (зацеп без длины) или Tip-On: створке с
  // ручкой она даёт «огрызок» 64 мм (приёмка 23.10) — берём длину по умолчанию.
  const stored = Number(config.handleLength) > 0 ? Number(config.handleLength) : undefined;
  return clamp(
    Math.round(stored ?? defaultHandleLengthMm(config.handle, availableSpan)),
    range.min,
    range.max
  );
};

export const handleCenterRangeMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  const orientation = handleOrientation(config);
  const length = handleLengthMm(config, doorWidthMm, doorHeightMm);
  const halfExtent = orientation === "vertical" ? length / 2 : 18;
  const minimum = Math.min(
    Math.max(30, halfExtent + 24),
    Math.max(30, doorHeightMm / 2)
  );
  return {
    min: Math.round(minimum),
    max: Math.round(Math.max(minimum, doorHeightMm - minimum))
  };
};

export const handleCenterYmm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  const range = handleCenterRangeMm(config, doorWidthMm, doorHeightMm);
  return clamp(
    Math.round(config.handleCenterY ?? doorHeightMm * 0.52),
    range.min,
    range.max
  );
};

export const handleEdgeOffsetRangeMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  const maximum =
    handleOrientation(config) === "horizontal"
      ? doorWidthMm -
        handleLengthMm(config, doorWidthMm, doorHeightMm) -
        MIN_HANDLE_EDGE_OFFSET_MM
      : doorWidthMm / 2 - 20;
  return {
    min: MIN_HANDLE_EDGE_OFFSET_MM,
    max: Math.max(MIN_HANDLE_EDGE_OFFSET_MM, Math.round(maximum))
  };
};

export const handleEdgeOffsetMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm = 900
) => {
  const range = handleEdgeOffsetRangeMm(config, doorWidthMm, doorHeightMm);
  return clamp(
    Math.round(
      config.handleEdgeOffset ?? Math.max(42, doorWidthMm * 0.13)
    ),
    range.min,
    range.max
  );
};

export const handleCenterFromOpeningEdgeMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) =>
  handleEdgeOffsetMm(config, doorWidthMm, doorHeightMm) +
  (handleOrientation(config) === "horizontal"
    ? handleLengthMm(config, doorWidthMm, doorHeightMm) / 2
    : 0);

/** Длина ручки сверх стандартных 220 мм — за неё доплата по прайсу. У Tip-On и зацепа её нет. */
export const HANDLE_STANDARD_LENGTH_MM = 220;

export const handleExtraLengthMm = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  if (config.handle === "push" || config.handle === "glassGrip") return 0;
  const length = config.handle === "integrated"
    ? integratedHandleLengthMm(config, doorWidthMm, doorHeightMm)
    : handleLengthMm(config, doorWidthMm, doorHeightMm);
  return Math.max(0, length - HANDLE_STANDARD_LENGTH_MM);
};

export const handleTechnicalDescription = (
  config: HandleGeometry,
  doorWidthMm: number,
  doorHeightMm: number
) => {
  if (config.handle === "push") return HANDLES.push.name;
  if (config.handle === "glassGrip") return `${HANDLES.glassGrip.name}: зацеп по всей высоте створки`;
  if (config.handle === "integrated") {
    const style = integratedHandleStyle(config);
    const length = integratedHandleLengthMm(config, doorWidthMm, doorHeightMm);
    return `${HANDLES.integrated.name}: паз в торце ${INTEGRATED_HANDLE_STYLES[style].short}, ${length} мм${
      style === "edgePart" ? `, центр ${handleCenterYmm(config, doorWidthMm, doorHeightMm)} мм от низа` : ""}`;
  }
  const orientation = handleOrientation(config);
  return `${HANDLES[config.handle].name}, ${handleLengthMm(
    config,
    doorWidthMm,
    doorHeightMm
  )} мм, ${orientation === "vertical" ? "вертикально" : "горизонтально"}, центр ${handleCenterYmm(
    config,
    doorWidthMm,
    doorHeightMm
  )} мм от низа, отступ ${handleEdgeOffsetMm(
    config,
    doorWidthMm,
    doorHeightMm
  )} мм от открывающего края`;
};

/**
 * Насколько ручка выступает перед лицом фасада, мм — для столкновений дверей
 * (29.10): скоба — по её 3D-модели; интегрированная, push-to-open, зацеп
 * стеклянной двери и врезная — без выноса.
 */
export const handleProjectionMm = (handle: HandleType | undefined) =>
  handle === "bar" ? 19 : handle === "profile" ? 19.5 : 0;
